/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useRef } from "react"
import { IComponentProp } from "@cd2/tools/build/es6"
import { Popover, PopoverRef } from "./Popover"

interface IProps {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  padding?: number
  showBorder?: boolean
  isLight?: boolean
  disabled?: boolean
  interactive?: boolean
  block?: boolean
  trigger?: "click"
  maxWidth?: number
  placement?: IComponentProp<typeof Popover, "placement">
  xRef?: React.MutableRefObject<PopoverRef | null>
  onShown?: () => void
  noMaxWidth?: boolean
}

export function QuickToolTip({
  children,
  content,
  className,
  padding = 12,
  showBorder,
  isLight,
  disabled,
  interactive,
  block,
  trigger,
  maxWidth,
  placement,
  xRef,
  noMaxWidth,
  onShown,
}: IProps) {
  const internalRef = useRef<PopoverRef>(null)

  const anchorChildCss = css`
    display: ${block ? "block" : "inline-block"};
    ${showBorder &&
    css`
      text-decoration: underline dashed #7d7d7d;
      text-underline-offset: 3px;
    `}
  `

  const contentCss = quickTooltipContentCss({ isLight, padding, noMaxWidth })

  if (disabled) {
    return (
      <div
        css={css`
          display: ${block ? "block" : "inline-block"};
          font-weight: normal;
          white-space: normal;
        `}
      >
        <div className={className} css={anchorChildCss}>
          {children}
        </div>
      </div>
    )
  }

  return (
    <div
      css={css`
        display: ${block ? "block" : "inline-block"};
        font-weight: normal;
        white-space: normal;
      `}
    >
      <Popover
        ref={xRef ?? internalRef}
        trigger={trigger === "click" ? "click" : "hover"}
        delay={trigger === "click" ? [0, 0] : [500, 0]}
        placement={placement ?? "bottom"}
        maxWidth={noMaxWidth ? undefined : maxWidth}
        interactive={interactive === true}
        inline={!block}
        onShow={onShown}
        contentCss={contentCss}
        getContent={() => <div>{content}</div>}
      >
        <div className={className} css={anchorChildCss}>
          {children}
        </div>
      </Popover>
    </div>
  )
}

export function quickTooltipContentCss({
  isLight,
  padding,
  noMaxWidth,
}: {
  padding: number
  isLight?: boolean
  noMaxWidth?: boolean
}) {
  return css`
    background-color: ${isLight ? "white" : "#333"};
    border: 1px solid ${isLight ? "#bbb" : "#333"};
    color: ${isLight ? "#333" : "white"};
    padding: ${padding}px;
    font-size: 13px;
    line-height: 1.4;
    ${noMaxWidth &&
    css`
      width: max-content;
    `}
  `
}
